import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2, Search } from "lucide-react";
import { listAdvertisers } from "@/api/commerce";
import type { Advertiser } from "@/types/commerce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Async typeahead picker for advertisers.
 *
 * Three value states:
 *   - `undefined`  → "All" (no filter applied) — only available when
 *                    `includeAll` is true. Used by list-page filter bars.
 *   - `null`       → "Unassigned" (filter to rows with no advertiser) —
 *                    only available when `includeUnassigned` is true. Used
 *                    by reassign popovers to clear ownership.
 *   - `number`     → A specific advertiser id.
 *
 * The full list is fetched once (advertisers are small, O(10s)) and filtered
 * client-side with a 200ms debounce so the dropdown feels instant.
 */
export interface AdvertiserSelectProps {
    value: number | null | undefined;
    onChange: (next: number | null | undefined) => void;
    placeholder?: string;
    includeUnassigned?: boolean;
    includeAll?: boolean;
    includeArchived?: boolean;
    disabled?: boolean;
    className?: string;
    "data-testid"?: string;
}

export function AdvertiserSelect({
    value,
    onChange,
    placeholder = "Select advertiser…",
    includeUnassigned = true,
    includeAll = true,
    includeArchived = false,
    disabled = false,
    className,
    "data-testid": testId,
}: AdvertiserSelectProps): React.ReactNode {
    const [open, setOpen] = React.useState(false);
    const [rawQuery, setRawQuery] = React.useState("");
    const [debouncedQuery, setDebouncedQuery] = React.useState("");

    React.useEffect(() => {
        const handle = window.setTimeout(() => setDebouncedQuery(rawQuery), 200);
        return () => window.clearTimeout(handle);
    }, [rawQuery]);

    const { data: advertisers, isLoading, isError, refetch } = useQuery({
        queryKey: ["advertisers", { includeArchived }],
        queryFn: () => listAdvertisers({ includeArchived }),
        staleTime: 30_000,
    });

    const selected = React.useMemo<Advertiser | null>(() => {
        if (typeof value !== "number") return null;
        return advertisers?.find((a) => a.id === value) ?? null;
    }, [advertisers, value]);

    const filtered = React.useMemo(() => {
        if (!advertisers) return [];
        const q = debouncedQuery.trim().toLowerCase();
        if (!q) return advertisers;
        return advertisers.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                a.slug.toLowerCase().includes(q) ||
                (a.contactEmail ?? "").toLowerCase().includes(q),
        );
    }, [advertisers, debouncedQuery]);

    const triggerLabel = (() => {
        if (value === undefined && includeAll) return "All advertisers";
        if (value === null && includeUnassigned) return "Unassigned";
        if (selected) return selected.name;
        if (typeof value === "number") return `#${value}`;
        return placeholder;
    })();

    const triggerIsPlaceholder = value === undefined && !includeAll && !selected;

    const handlePick = (next: number | null | undefined): void => {
        onChange(next);
        setOpen(false);
        setRawQuery("");
    };

    return (
        <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-haspopup="listbox"
                    aria-label="Select advertiser"
                    disabled={disabled}
                    data-testid={testId ?? "advertiser-select"}
                    className={cn(
                        "w-full justify-between font-normal",
                        triggerIsPlaceholder && "text-muted-foreground",
                        className,
                    )}
                >
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                className="w-[var(--radix-popover-trigger-width)] p-0"
                align="start"
                data-testid={testId ? `${testId}-content` : "advertiser-select-content"}
            >
                <div className="flex items-center gap-2 border-b px-3 py-2">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <Input
                        autoFocus
                        value={rawQuery}
                        onChange={(e) => setRawQuery(e.target.value)}
                        placeholder="Search by name, slug, or email…"
                        className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
                        data-testid={testId ? `${testId}-search` : "advertiser-select-search"}
                    />
                </div>
                <div
                    role="listbox"
                    className="max-h-72 overflow-auto py-1"
                >
                    {includeAll && (
                        <OptionRow
                            label="All advertisers"
                            hint="No filter"
                            selected={value === undefined}
                            onSelect={() => handlePick(undefined)}
                            testId={testId ? `${testId}-opt-all` : "advertiser-select-opt-all"}
                        />
                    )}
                    {includeUnassigned && (
                        <OptionRow
                            label="Unassigned"
                            hint="No advertiser"
                            selected={value === null}
                            onSelect={() => handlePick(null)}
                            testId={testId ? `${testId}-opt-unassigned` : "advertiser-select-opt-unassigned"}
                        />
                    )}
                    {(includeAll || includeUnassigned) && <div className="my-1 border-t" />}

                    {isLoading && (
                        <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading advertisers…
                        </div>
                    )}
                    {isError && (
                        <div className="px-3 py-4 text-sm text-destructive">
                            Failed to load advertisers.{" "}
                            <button
                                type="button"
                                className="underline underline-offset-2"
                                onClick={() => void refetch()}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    {!isLoading && !isError && filtered.length === 0 && (
                        <div className="px-3 py-4 text-sm text-muted-foreground">
                            No advertisers match “{debouncedQuery}”.
                        </div>
                    )}
                    {!isLoading &&
                        !isError &&
                        filtered.map((a) => (
                            <OptionRow
                                key={a.id}
                                label={a.name}
                                hint={`${a.slug}${a.status === "archived" ? " · archived" : ""}`}
                                selected={value === a.id}
                                onSelect={() => handlePick(a.id)}
                                testId={`advertiser-select-opt-${a.id}`}
                            />
                        ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function OptionRow({
    label,
    hint,
    selected,
    onSelect,
    testId,
}: {
    label: string;
    hint?: string;
    selected: boolean;
    onSelect: () => void;
    testId?: string;
}): React.ReactNode {
    return (
        <button
            type="button"
            role="option"
            aria-selected={selected}
            onClick={onSelect}
            data-testid={testId}
            className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground",
                selected && "bg-accent/50",
            )}
        >
            <Check
                className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")}
                aria-hidden="true"
            />
            <span className="flex-1 truncate">{label}</span>
            {hint && (
                <span className="ml-2 shrink-0 text-xs text-muted-foreground">{hint}</span>
            )}
        </button>
    );
}
